<script lang="ts">
  import { inertia, type LinkParameters } from '../utils/inertia'
  import type { Snippet } from 'svelte'

  type Variant =
    | 'button-brand'
    | 'button-brand-outline'
    | 'button-warning'
    | 'button-warning-outline'
    | 'button-success'
    | 'button-success-outline'
    | 'button-danger'
    | 'button-danger-outline'
    | 'button-neutral'
    | 'button-neutral-outline'
  type VisitOptions = Omit<LinkParameters, 'href'>

  let {
    href,
    variant,
    target,
    rel,
    download,
    current = false,
    start,
    end,
    children,
    ...visitOptions
  }: {
    href: string
    variant: Variant
    target?: '_blank' | '_parent' | '_self' | '_top'
    rel?: string
    download?: string | boolean
    current?: boolean
    start?: Snippet
    end?: Snippet
    children: Snippet
  } & VisitOptions = $props()
</script>

<a
  {href}
  {target}
  {rel}
  {download}
  aria-current={current ? 'true' : undefined}
  {@attach inertia({ href, ...visitOptions })}
  class={['button-link', {
    '-variant-button-brand': variant === 'button-brand',
    '-variant-button-brand-outline': variant === 'button-brand-outline',
    '-variant-button-warning': variant === 'button-warning',
    '-variant-button-warning-outline': variant === 'button-warning-outline',
    '-variant-button-success': variant === 'button-success',
    '-variant-button-success-outline': variant === 'button-success-outline',
    '-variant-button-danger': variant === 'button-danger',
    '-variant-button-danger-outline': variant === 'button-danger-outline',
    '-variant-button-neutral': variant === 'button-neutral',
    '-variant-button-neutral-outline': variant === 'button-neutral-outline',
  }]}
>
  {#if start}{@render start()}{/if}
  {@render children()}
  {#if end}{@render end()}{/if}
</a>

<style>
  .button-link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--font-size-4);
    font-weight: var(--font-weight-3);
    padding: var(--space-4) var(--space-6);
    border-radius: var(--radius-6);
    border: var(--border-2) solid transparent;
    text-decoration: none;
    transition:
      background-color var(--transition-medium),
      color var(--transition-medium),
      border-color var(--transition-medium);
    width: fit-content;
  }

  .button-link:focus-visible {
    outline: none;
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-neutral) 50%, transparent);
  }

  .button-link.-variant-button-brand {
    background-color: var(--color-variant-brand);
    color: var(--color-variant-brand-muted);
  }

  .button-link.-variant-button-brand-outline {
    background-color: transparent;
    color: var(--color-variant-brand);
    border-color: var(--color-variant-brand);
  }

  .button-link.-variant-button-brand-outline:hover {
    background-color: var(--color-variant-brand);
    color: var(--color-variant-brand-muted);
  }

  .button-link.-variant-button-brand:focus-visible,
  .button-link.-variant-button-brand-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-brand) 50%, transparent);
  }

  .button-link.-variant-button-warning {
    background-color: var(--color-variant-warning);
    color: var(--color-variant-warning-muted);
  }

  .button-link.-variant-button-warning-outline {
    background-color: transparent;
    color: var(--color-variant-warning);
    border-color: var(--color-variant-warning);
  }

  .button-link.-variant-button-warning-outline:hover {
    background-color: var(--color-variant-warning);
    color: var(--color-variant-warning-muted);
  }

  .button-link.-variant-button-warning:focus-visible,
  .button-link.-variant-button-warning-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-warning) 50%, transparent);
  }

  .button-link.-variant-button-success {
    background-color: var(--color-variant-success);
    color: var(--color-variant-success-muted);
  }

  .button-link.-variant-button-success-outline {
    background-color: transparent;
    color: var(--color-variant-success);
    border-color: var(--color-variant-success);
  }

  .button-link.-variant-button-success-outline:hover {
    background-color: var(--color-variant-success);
    color: var(--color-variant-success-muted);
  }

  .button-link.-variant-button-success:focus-visible,
  .button-link.-variant-button-success-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-success) 50%, transparent);
  }

  .button-link.-variant-button-danger {
    background-color: var(--color-variant-danger);
    color: var(--color-variant-danger-muted);
  }

  .button-link.-variant-button-danger-outline {
    background-color: transparent;
    color: var(--color-variant-danger);
    border-color: var(--color-variant-danger);
  }

  .button-link.-variant-button-danger-outline:hover {
    background-color: var(--color-variant-danger);
    color: var(--color-variant-danger-muted);
  }

  .button-link.-variant-button-danger:focus-visible,
  .button-link.-variant-button-danger-outline:focus-visible {
    box-shadow: 0 0 0 var(--space-2) color-mix(in oklch, var(--color-variant-danger) 50%, transparent);
  }

  .button-link.-variant-button-neutral {
    background-color: var(--color-variant-neutral);
    color: var(--color-variant-neutral-muted);
  }

  .button-link.-variant-button-neutral-outline {
    background-color: transparent;
    color: var(--color-variant-neutral);
    border-color: var(--color-variant-neutral);
  }

  .button-link.-variant-button-neutral-outline:hover {
    background-color: var(--color-variant-neutral);
    color: var(--color-variant-neutral-muted);
  }
</style>
